import {AfterViewInit, Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy, Optional} from '@angular/core';
import {FroalaTemplateVariablesService} from './froala-template-variables.service';
import {TranslationService} from '@ngmedax/translation';
import {TRANSLATION_EDITOR_SCOPE} from '../../constants';
import {CONDITION_TOKEN_SELECTOR, decorateConditionTokens} from './template-condition-tokens';
import {decorateVariableTokens, VARIABLE_TOKEN_SELECTOR} from './template-variable-tokens';

/** Presents readable variable tokens and highlights complex expressions without changing their stored values. */
@Directive({selector: '[qaTemplateVariableHighlight]'})
export class TemplateVariableHighlightDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input() qaTemplateVariableHighlight = false;
  @Input() qaTemplateVariablePreview = false;
  @Input() qaTemplateVariableLocale = 'de_DE';
  @Input() qaTemplateVariableLabels: {[path: string]: string} = {};
  private observer: MutationObserver;
  private frame: number;
  private ranges: Range[] = [];
  private highlight: any;

  /** Injects the editor host and keeps visual updates outside Angular change detection. */
  public constructor(private element: ElementRef<HTMLElement>, private zone: NgZone,
    @Optional() private translation: TranslationService,
    @Optional() private variables: FroalaTemplateVariablesService) {}

  /** Observes both the lazy preview and the live editor, including undo and source edits. */
  public ngAfterViewInit(): void {
    const browser = window as any;
    if (!this.qaTemplateVariableHighlight) {
      return;
    }
    if (browser.Highlight && browser.CSS?.highlights) {
      this.highlight = browser.CSS.highlights.get('qa-template-variable') || new browser.Highlight();
      browser.CSS.highlights.set('qa-template-variable', this.highlight);
    }
    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => this.schedule());
      this.observe();
      this.element.nativeElement.addEventListener('click', this.onTokenInteraction, true);
      this.element.nativeElement.addEventListener('keydown', this.onTokenInteraction, true);
      this.schedule();
    });
  }

  /** Refreshes token labels when the current questionnaire or locale changes. */
  public ngOnChanges(): void {
    this.observer && this.zone.runOutsideAngular(() => this.schedule());
  }

  /** Releases ranges when switching questions or closing the editor. */
  public ngOnDestroy(): void {
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
    this.clear();
    this.element.nativeElement.removeEventListener('click', this.onTokenInteraction, true);
    this.element.nativeElement.removeEventListener('keydown', this.onTokenInteraction, true);
  }

  /** Coalesces editor mutations into one update per frame. */
  private schedule(): void {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      this.observer.disconnect();
      this.clear();
      const roots = this.element.nativeElement.querySelectorAll('.fr-element, .qa-template-preview');
      const hasTemplates = Array.from(roots).some(root => root.textContent.indexOf('{{') !== -1);
      const labels = this.qaTemplateVariablePreview && hasTemplates && this.variables
        ? this.variables.getLabels(this.qaTemplateVariableLocale) : this.qaTemplateVariableLabels;
      roots.forEach(root => {
        if (root.querySelector('.fr-marker, .fr-tmp')) {
          return;
        }
        const selection = window.getSelection();
        const range = selection.rangeCount && selection.getRangeAt(0);
        const restore = range && root.contains(range.commonAncestorContainer);
        const start = restore ? this.textOffset(root, range.startContainer, range.startOffset) : 0;
        const end = restore ? this.textOffset(root, range.endContainer, range.endOffset) : 0;
        const conditionChanged = decorateConditionTokens(root, labels, text =>
          this.translation ? this.translation.translate({text, scope: TRANSLATION_EDITOR_SCOPE}) : text);
        const changed = decorateVariableTokens(root, labels) || conditionChanged;
        if (this.qaTemplateVariablePreview) {
          root.querySelectorAll(`${VARIABLE_TOKEN_SELECTOR}, ${CONDITION_TOKEN_SELECTOR}`).forEach(token => {
            token.removeAttribute('tabindex');
            token.removeAttribute('role');
          });
        }
        if (restore && changed) {
          const restored = document.createRange();
          this.setBoundary(restored, root, start, true);
          this.setBoundary(restored, root, end, false);
          selection.removeAllRanges();
          selection.addRange(restored);
        }
        this.highlight && this.highlightExpressions(root);
      });
      this.observe();
    });
  }

  /** Watches authored content but excludes the directive's own presentation mutations. */
  private observe(): void {
    this.observer.observe(this.element.nativeElement, {childList: true, characterData: true, subtree: true});
  }

  /** Toggles the original expression on click or keyboard activation. */
  private onTokenInteraction = (event: MouseEvent | KeyboardEvent): void => {
    if (this.qaTemplateVariablePreview) { return; }
    const condition = (event.target as Element).closest(CONDITION_TOKEN_SELECTOR) as HTMLElement;
    if (condition && (!(event instanceof KeyboardEvent) || event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      event.stopPropagation();
      condition.dispatchEvent(new CustomEvent('qa-condition-edit', {bubbles: true, detail: {id: condition.dataset.qaConditionId}}));
      return;
    }
    const token = (event.target as Element).closest(VARIABLE_TOKEN_SELECTOR);
    if (!token || (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    // editors open the picker for the token; read only views keep showing the raw expression.
    // dispatched inside the zone, so a lazy field renders its editor right away
    const edit = new CustomEvent('qa-variable-edit', {bubbles: true, cancelable: true});
    if (!this.zone.run(() => token.dispatchEvent(edit))) {
      return;
    }
    token.classList.toggle('qa-variable-show-code');
    if (token.closest('.fr-element')?.getAttribute('contenteditable') === 'true') {
      const range = document.createRange();
      range.setStartAfter(token);
      range.collapse(true);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  /** Measures a selection boundary using the original variable text, independent of display labels. */
  private textOffset(root: Element, node: Node, offset: number): number {
    const range = document.createRange();
    range.selectNodeContents(root);
    range.setEnd(node, offset);
    return range.toString().length;
  }

  /** Restores a boundary beside atomic tokens instead of placing the caret inside their hidden text. */
  private setBoundary(range: Range, root: Element, offset: number, start: boolean): void {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node: Node;
    while ((node = walker.nextNode())) {
      if (offset > node.textContent.length) {
        offset -= node.textContent.length;
        continue;
      }
      const token = node.parentElement.closest(`${VARIABLE_TOKEN_SELECTOR}, ${CONDITION_TOKEN_SELECTOR}`);
      if (token) {
        if (offset === 0) {
          start ? range.setStartBefore(token) : range.setEndBefore(token);
        } else {
          start ? range.setStartAfter(token) : range.setEndAfter(token);
        }
      } else {
        start ? range.setStart(node, offset) : range.setEnd(node, offset);
      }
      return;
    }
    start ? range.setStart(root, root.childNodes.length) : range.setEnd(root, root.childNodes.length);
  }

  /** Removes only this editor's ranges from the shared highlight registry. */
  private clear(): void {
    this.ranges.forEach(range => this.highlight.delete(range));
    this.ranges = [];
  }

  /** Resolves expressions across text nodes so formatting inside a variable remains visible. */
  private highlightExpressions(root: Element): void {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes: {node: Node; start: number; end: number}[] = [];
    let content = '';
    let node: Node;
    while ((node = walker.nextNode())) {
      const start = content.length;
      content += node.textContent;
      nodes.push({node, start, end: content.length});
    }
    const pattern = /\{\{\{?[^{}]+\}\}\}?/g;
    let match: RegExpExecArray;
    while ((match = pattern.exec(content))) {
      const start = nodes.find(entry => entry.end > match.index);
      const endOffset = match.index + match[0].length;
      const end = nodes.find(entry => entry.end >= endOffset);
      if (!start || !end) {
        continue;
      }
      const range = document.createRange();
      range.setStart(start.node, match.index - start.start);
      range.setEnd(end.node, endOffset - end.start);
      this.highlight.add(range);
      this.ranges.push(range);
    }
  }
}
